/**
 * Admin users list page for /admin/users route.
 * Lists all users in the platform for admin management.
 * Uses KiiList with server-side search and pagination.
 */

import { useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { adminGetAllUsers, type AdminUser } from "../services/naraDoor";
import { adminRoutes, publicRoutes } from "../lib/routes";
import { Users, Mail, Phone } from "lucide-react";
import { KiiList, type KiiListColumn } from "../components/kii";
import { TableRow, TableCell } from "../components/ui/table";

function getUserDisplayName(user: AdminUser, unknownLabel: string) {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user.email || user.phone || unknownLabel;
}

const columns: KiiListColumn[] = [
  { key: "user", label: "User" },
  { key: "contact", label: "Contact" },
  { key: "workspaces", label: "Workspaces" },
  { key: "created", label: "Created" },
];

export function AdminUsers() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 20;

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ["adminUsers", skip, limit, search],
    queryFn: () => adminGetAllUsers(skip, limit, search || undefined),
    enabled: isAdmin,
  });

  const users = data?.users || [];
  const total = data?.total || 0;

  const handleSearch = useCallback((query: string) => {
    setSearch(query.trim());
    setSkip(0);
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchInput(query);
  }, []);

  const handlePageChange = useCallback((newSkip: number) => {
    setSkip(newSkip);
  }, []);

  const handleRowClick = useCallback((user: AdminUser) => {
    navigate(adminRoutes.user(user.id));
  }, [navigate]);

  return (
    <div>
      <PageHeader
        title={t("adminUsers.title")}
        description={t("adminUsers.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminUsers.breadcrumb.dashboard"), href: publicRoutes.home },
          { label: t("adminUsers.breadcrumb.admin") },
          { label: t("adminUsers.breadcrumb.users") },
        ]}
      />

      <KiiList<AdminUser>
        showSearch
        searchPlaceholder={t("adminUsers.searchPlaceholder")}
        searchQuery={searchInput}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearch}
        columns={columns}
        data={users}
        isLoading={isLoading}
        emptyMessage={search ? t("adminUsers.empty.searchHint") : t("adminUsers.empty.noUsers")}
        emptyIcon={<Users className="h-10 w-10 text-muted-foreground" />}
        showCount={true}
        countLabel={(count) => `${count} user${count !== 1 ? "s" : ""}`}
        onRowClick={handleRowClick}
        serverPagination={{
          totalCount: total,
          skip,
          limit,
        }}
        onPageChange={handlePageChange}
        renderRow={(user) => (
          <TableRow key={user.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt=""
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  ) : (
                    <Users className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <span className="font-medium">
                  {getUserDisplayName(user, t("adminUsers.unknown"))}
                </span>
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-0.5">
                {user.email && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    {user.email}
                  </div>
                )}
                {user.phone && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    {user.phone}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {user.workspace_count ?? "\u2014"}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(user.created_at, "date")}
            </TableCell>
          </TableRow>
        )}
      />
    </div>
  );
}
